import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { supabase } from '../../../lib/supabase';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select } from '../../../components/ui/select';
import { useUser } from '../../../hooks/useUser';

interface WithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const WithdrawalModal: React.FC<WithdrawalModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useUser();
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('mobile_money');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [bankDetails, setBankDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setIsSubmitting(true);

      const withdrawalData = {
        user_id: user.id,
        amount: parseFloat(amount),
        payment_method: paymentMethod,
        phone_number: paymentMethod === 'mobile_money' ? phoneNumber : null,
        bank_details: paymentMethod === 'bank_transfer' ? { details: bankDetails } : null,
        status: 'pending'
      };

      const { error } = await supabase
        .from('withdrawals')
        .insert([withdrawalData]);

      if (error) throw error;

      onSuccess();
      resetForm();
    } catch (error) {
      console.error('Error creating withdrawal:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setAmount('');
    setPaymentMethod('mobile_money');
    setPhoneNumber('');
    setBankDetails('');
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/80" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md rounded-2xl bg-gray-900 border border-white/10 p-6">
          <Dialog.Title className="text-lg font-medium text-white mb-4">
            Demande de retrait
          </Dialog.Title>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Montant
              </label>
              <Input
                type="number"
                min="1000"
                step="100"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                className="bg-white/5 border-white/10 text-white"
                placeholder="Montant minimum : 1000 XAF"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Méthode de paiement
              </label>
              <Select
                value={paymentMethod}
                onValueChange={setPaymentMethod}
                options={[
                  { value: 'mobile_money', label: 'Mobile Money' },
                  { value: 'bank_transfer', label: 'Virement bancaire' }
                ]}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            {paymentMethod === 'mobile_money' && (
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Numéro de téléphone
                </label>
                <Input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Ex: +237 6XXXXXXXX"
                  pattern="^\+237[0-9]{9}$"
                  title="Format : +237 suivi de 9 chiffres"
                />
                <p className="mt-1 text-xs text-gray-400">Format : +237 suivi de votre numéro</p>
              </div>
            )}

            {paymentMethod === 'bank_transfer' && (
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Détails bancaires
                </label>
                <textarea
                  value={bankDetails}
                  onChange={(e) => setBankDetails(e.target.value)}
                  required
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                  rows={3}
                  placeholder="Nom de la banque, numéro de compte, etc."
                />
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="border-white/10 text-gray-400 hover:bg-white/5"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-primary hover:bg-primary/90"
              >
                {isSubmitting ? 'Envoi...' : 'Envoyer la demande'}
              </Button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default WithdrawalModal;
